import {
  paymentAvailability,
  distributionLogicOptions,
} from "constants/paymentPlanConstants";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { useState, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

import { checkForOverlappingRanges } from "../../helpers/helpers";
import {
  ISetting,
  CustomPaymentPlanItem,
} from "../PaymentAndRemit/CustomPaymentPlanItem";

const StyledButton = styled(Button)`
  padding: 0px;
  width: fit-content;
`;

export function PaymentPlan() {
  const { control, trigger, watch, setValue } = useFormContext<any>();
  const { handleOnBlurField } = useFormValidation();
  const [expanded, setExpanded] = useState(false);
  const paymentPlanCustomSettingsField = watch("paymentPlanCustomSettings");
  const [overlappingRanges, setOverlappingRanges] = useState(false);

  const handleAddCustomSetting = () => {
    const newSetting: ISetting = {
      min: 0,
      max: null,
      enabled: false,
      paymentPlanDuration: 0,
      minDownPaymentType: "percent",
      minDownPaymentPercent: null,
      minDownPaymentFlat: null,
    };
    setValue("paymentPlanCustomSettings", [
      ...paymentPlanCustomSettingsField,
      newSetting,
    ]);
  };

  useEffect(() => {
    let hasOverlappingRanges = false;
    if (paymentPlanCustomSettingsField?.length) {
      hasOverlappingRanges = checkForOverlappingRanges({
        settings: paymentPlanCustomSettingsField,
      });
    }
    setOverlappingRanges(hasOverlappingRanges);
  }, [paymentPlanCustomSettingsField]);
  return (
    <div className="block bg-white border-round-xl border-1 border-gray-300 p-4">
      <button
        type="button"
        className="flex align-items-center justify-content-between w-full border-none bg-transparent p-0 cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="text-black font-bold text-lg m-0">Payment Plan</span>
        <i
          className={`pi pi-chevron-down text-gray-600 transition-duration-200 transition-all ${
            expanded ? "rotate-180" : ""
          }`}
          style={{ fontSize: "0.75rem" }}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="flex flex-column gap-4 mt-4">
          <div className="flex flex-column gap-4 border-bottom-1 border-gray-200 pb-4">
            <div className="flex gap-3">
              <Controller
                name="partialPaymentsEnabled"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="partialPaymentsEnabled"
                    checked={value || false}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">
                  Partial Payments
                </span>
                <span className="text-sm text-gray-500">
                  Enable partial payments
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-column gap-4 border-bottom-1 border-gray-200 pb-4">
            <div className="flex gap-3">
              <Controller
                name="patientResponsibilityEnabled"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="patientResponsibilityEnabled"
                    checked={value || false}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">
                  Patient Responsibility
                </span>
                <span className="text-sm text-gray-500">
                  Enable for Patient Responsibility
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <Controller
                name="edEnabled"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="edEnabled"
                    checked={value || false}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">ER</span>
                <span className="text-sm text-gray-500">Enable for ER</span>
              </div>
            </div>
            {/* <div className="col-12 md:col-4 flex gap-3 align-items-center">
                <Controller
                  name="bariatricsEnabled"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputSwitch
                      inputId="bariatricsEnabled"
                      checked={value || false}
                      onChange={(e) => onChange(e.value)}
                    />
                  )}
                />
                <span className="font-medium text-sm">
                  Enable for Bariatrics
                </span>
              </div> */}
            <div className="flex gap-3">
              <Controller
                name="gfeEnabled"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="gfeEnabled"
                    checked={value || false}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">
                  Clear Estimate Pro
                </span>
                <span className="text-sm text-gray-500">
                  Enable for Clear Estimate Pro
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <Controller
                name="bundledEnabled"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSwitch
                    inputId="bundledEnabled"
                    checked={value || false}
                    onChange={(e) => onChange(e.value)}
                  />
                )}
              />
              <div className="flex flex-column">
                <span className="font-medium text-sm mb-1">Clear Packages</span>
                <span className="text-sm text-gray-500">
                  Enable for Clear Packages
                </span>
              </div>
            </div>
          </div>
          <div className="grid formgrid">
            <div className="field flex flex-column col-12 md:col-3">
              <Label
                htmlFor="paymentPlanDuration"
                data-testid="paymentPlanMonthsLabel"
              >
                Default number of months
              </Label>

              <Controller
                name="paymentPlanDuration"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputNumber
                    id="paymentPlanDuration"
                    data-testid="months_PaymentPlan"
                    value={value}
                    onValueChange={onChange}
                    min={1}
                    max={60}
                    placeholder="Enter number of months"
                    className="w-full"
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-3">
              <Label
                htmlFor="paymentPlanMinDownPaymentPercent"
                data-testid="paymentPlanMinDownPaymentPercent"
              >
                Default minimum first payment percent
              </Label>

              <Controller
                name="paymentPlanMinDownPaymentPercent"
                control={control}
                render={({ field: { onChange, value, onBlur } }) => (
                  <InputNumber
                    value={value}
                    suffix="%"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanMinDownPaymentPercent",
                        trigger,
                      })
                    }
                    inputId="paymentPlanMinDownPaymentPercent"
                    data-testid="minDownPaymentPercent_PaymentPlan"
                    onValueChange={onChange}
                    min={0}
                    max={100}
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-3 mb-4">
              <Label
                htmlFor="paymentPlanAvailability"
                data-testid="paymentPlanAvailability"
              >
                Payment plan availability
              </Label>

              <Controller
                name="paymentPlanAvailability"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="paymentPlanAvailability"
                    data-testid="paymentPlanAvailability_PaymentPlan"
                    value={value}
                    onChange={onChange}
                    options={Object.values(paymentAvailability)}
                    className="w-full"
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-3 mb-4">
              <Label
                htmlFor="providerDistributionLogic"
                data-testid="providerDistributionLogic"
              >
                Provider Distribution Logic
              </Label>

              <Controller
                name="providerDistributionLogic"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="providerDistributionLogic"
                    data-testid="providerDistributionLogic_PaymentPlan"
                    value={value}
                    onChange={onChange}
                    options={Object.values(distributionLogicOptions)}
                    optionLabel="label"
                    className="w-full"
                  />
                )}
              />
            </div>
          </div>
          {paymentPlanCustomSettingsField?.map(
            (setting: ISetting, index: number) => (
              <CustomPaymentPlanItem
                key={index}
                setting={setting}
                settings={paymentPlanCustomSettingsField}
                setSettings={(settings: ISetting[]) =>
                  setValue("paymentPlanCustomSettings", settings)
                }
                index={index}
              />
            )
          )}
          {overlappingRanges && (
            <p className="mb-4 text-orange-500">
              Your custom settings have overlapping amount ranges. Please,
              remove the overlapping settings or change the amounts.
            </p>
          )}
          <div className="w-full flex flex-column gap-2">
            <StyledButton
              className="p-button-link gap-2"
              icon="pi pi-plus"
              onClick={handleAddCustomSetting}
              type="button"
            >
              <p className="small-text">Add Custom Setting</p>
            </StyledButton>
          </div>
        </div>
      )}
    </div>
  );
}
